import { Button, Modal } from 'antd';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { moveFolder } from '@/services/api/folderService';
import { moveDocument } from '@/services/api/documentService';
import TableView from '@/components/folders/TableView';
import { Documentcolumn } from '@/components/columns/DocumentsColumn';
import { useAppContext } from '@/context';
import { DocumentView } from '../Sidebar/DocumentView';
import { AppConstants } from '@/utils/constants';
import { useTranslation } from 'react-i18next';
import useMomentWithTimezone from '@/hooks/timezone';
import { ArrowRightOutlined } from '@ant-design/icons';
interface ModalMoveFileProps {
  documentView: DocumentView;
  movingNode: any;
  setMovingNode: any;
  usersInDocuments: any;
  setUsersInDocuments: any;
  open: boolean;
  onCancel: () => void;
}

const ModalMoveFile: FC<ModalMoveFileProps> = ({
  documentView,
  movingNode,
  setMovingNode,
  usersInDocuments,
  setUsersInDocuments,
  open,
  onCancel,
}) => {
  const { user } = useAppContext();
  const momentWithTimezone = useMomentWithTimezone();
  const { t } = useTranslation();
  const [_, setCount] = useState<number>(0);
  const [tableDocumentView] = useState(
    () => new DocumentView(user || {}, () => setCount(prev => prev + 1)),
  );
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [targetNode, setTargetNode] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<any>(false);

  useEffect(() => {
    if (documentView.treeData) {
      tableDocumentView.treeData = documentView.treeData;
      tableDocumentView.tableData = documentView.treeData.filter(
        (item: any) => !item.parent,
      );
      setTargetNode(null);
      setCount(prev => prev + 1);
    }
  }, [documentView, movingNode, open]);

  const buildParentChain = (targetNode: any, treeData: any[]): any => {
    const findNodeWithPath = (
      nodeId: string,
      nodes: any[],
      path: any[] = [],
    ): { node: any; path: any[] } | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return { node, path };
        }
        if (node.children) {
          const result = findNodeWithPath(nodeId, node.children, [
            ...path,
            node,
          ]);
          if (result) return result;
        }
      }
      return null;
    };

    const result = findNodeWithPath(targetNode.id, treeData);
    if (!result) return null;

    const { node, path } = result;

    if (path.length === 0) {
      return { ...node, parent: null };
    }

    const buildChain = (index: number): any => {
      if (index < 0) {
        return { ...node, parent: null };
      }
      const parentNode = { ...path[index] };
      parentNode.parent = index === 0 ? null : buildChain(index - 1);
      return parentNode;
    };

    const targetNodeWithParent = { ...node };
    targetNodeWithParent.parent = buildChain(path.length - 1);

    return targetNodeWithParent;
  };

  const handleMove = async () => {
    setIsLoading(true);
    const targetFolder =
      targetNode === null || targetNode?.isBack || !targetNode?.id
        ? ''
        : targetNode?.id;
    let response;
    if (movingNode.isLeaf) {
      response = await moveDocument({
        document_id: movingNode.id,
        folder_id: targetFolder,
      });
    } else {
      response = await moveFolder({
        folder_id: movingNode.id,
        folder_id_to: targetFolder,
      });
    }
    if (!response.success) {
      setIsLoading(false);
      return;
    }
    documentView.moveBranch(movingNode, targetFolder);
    setConfirmModal(false);
    setIsLoading(false);
    onCancel();
  };

  const handleCancel = () => {
    setConfirmModal(false);
  };

  const onLoadData = async (node: any, isBack: boolean = false) => {
    if (isBack) {
      if (node === null) {
        tableDocumentView.backToNode(null);
        tableDocumentView.tableData = tableDocumentView.treeData.filter(
          (item: any) => !item.parent,
        );
        setTargetNode(null);
        setCount(prev => prev + 1);
      } else {
        tableDocumentView.backToNode(node);
        const nodeWithParent = buildParentChain(
          node,
          tableDocumentView.treeData,
        );
        setTargetNode(nodeWithParent || node);
        setCount(prev => prev + 1);
      }
      return;
    }

    if (node.id == movingNode.id) {
      return;
    }

    if (node.children) {
      tableDocumentView.tableData = node.children;
      const nodeWithParent = buildParentChain(
        node,
        tableDocumentView.treeData,
      );
      setTargetNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setCount(prev => prev + 1);
    } else {
      await tableDocumentView.onLoadData(node);
      const updatedNode = tableDocumentView.findNodeInTree(
        node.id,
        tableDocumentView.treeData,
      );
      if (updatedNode?.children) {
        tableDocumentView.tableData = updatedNode.children;
      }
      const nodeWithParent = updatedNode
        ? buildParentChain(updatedNode, tableDocumentView.treeData)
        : buildParentChain(node, tableDocumentView.treeData);
      setTargetNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setCount(prev => prev + 1);
    }
  };

  const isMovable = (record: any): boolean => {
    const isLeaf = record?.isLeaf;
    const isMovingNode = record.id == movingNode.id;
    const isParentNode = record.id == movingNode?.parent?.id;
    if (isLeaf || isMovingNode || isParentNode) {
      return false;
    }
    return true;
  };

  return (
    <div>
      <Modal
        title={t('home.documents.modals.move.title')}
        width={'100%'}
        open={open}
        onCancel={onCancel}
        footer={null}
      >
        <TableView
          tableData={tableDocumentView.tableData}
          currentNode={targetNode}
          setCurrentNode={setTargetNode}
          usersInDocuments={usersInDocuments}
          setUsersInDocuments={setUsersInDocuments}
          onExpand={onLoadData}
          columns={[
            ...(Documentcolumn(
              t,
              onLoadData,
              user?.scopes?.includes(AppConstants.adminRole),
              momentWithTimezone,
            ) as any),
            {
              title: t('home.documents.table.action'),
              key: 'action',
              render: (_: any, record: any) => {
                if (record?.isBack) {
                  if (record.parent === null || record.parent === undefined) {
                    if (!movingNode?.parent?.id) {
                      return null;
                    }
                    return (
                      <Button
                        onClick={() => {
                          setTargetNode(null);
                          setConfirmModal(true);
                        }}
                        type="primary"
                      >
                        <ArrowRightOutlined />
                        {t('home.documents.modals.move.btn')}
                      </Button>
                    );
                  }
                  return null;
                }
                if (!isMovable(record)) {
                  return null;
                }
                return (
                  <Button
                    onClick={() => {
                      setTargetNode(record);
                      setConfirmModal(true);
                    }}
                    type="primary"
                  >
                    <ArrowRightOutlined />
                    {t('home.documents.modals.move.btn')}
                  </Button>
                );
              },
            },
          ]}
        />
      </Modal>
      <Modal
        title={t('home.documents.modals.move.confirmModal.title')}
        open={confirmModal}
        onOk={handleMove}
        onCancel={handleCancel}
        okText={t('home.documents.modals.move.confirmModal.confirm')}
        cancelText={t('home.documents.modals.move.confirmModal.cancel')}
        confirmLoading={isLoading}
      >
        <b>
          {t('home.documents.modals.move.confirmModal.content', {
            fileName: movingNode?.name,
            target:
              targetNode === null
                ? t('home.documents.home')
                : targetNode?.name,
          })}
        </b>
      </Modal>
    </div>
  );
};

export default ModalMoveFile;
